import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * The effects below intentionally run once on mount: each builds a
 * `gsap.context()` that is reverted on unmount, matching the pattern used by the
 * section components. The config arguments are object literals from the call
 * site, so listing them as dependencies would rebuild the animation on every
 * render — hence the scoped exhaustive-deps suppressions.
 */

interface ScrollTriggerConfig {
    trigger?: string | Element;
    start?: string;
    end?: string;
    toggleActions?: string;
    markers?: boolean;
}

interface AnimationConfig {
    from: gsap.TweenVars;
    to: gsap.TweenVars;
    scrollTrigger?: ScrollTriggerConfig;
    duration?: number;
    ease?: string;
    delay?: number;
}

export function useGsapScrollTrigger<T extends HTMLElement>(
    config: AnimationConfig
) {
    const ref = useRef<T>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const ctx = gsap.context(() => {
            gsap.fromTo(
                el,
                config.from,
                {
                    ...config.to,
                    duration: config.duration ?? 1,
                    ease: config.ease ?? 'power2.out',
                    delay: config.delay ?? 0,
                    scrollTrigger: {
                        trigger: el,
                        start: 'top 85%',
                        toggleActions: 'play none none none',
                        ...config.scrollTrigger,
                    },
                }
            );
        }, el);

        return () => ctx.revert();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return ref;
}

export function useGsapTimeline(
    buildTimeline: (tl: gsap.core.Timeline, el: HTMLElement) => void,
    scrollTriggerConfig?: ScrollTriggerConfig
) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: el,
                    start: 'top 85%',
                    toggleActions: 'play none none none',
                    ...scrollTriggerConfig,
                },
            });

            buildTimeline(tl, el);
        }, el);

        return () => ctx.revert();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return ref;
}

export function useStaggerAnimation<T extends HTMLElement>(
    childSelector: string,
    config: {
        from: gsap.TweenVars;
        to?: gsap.TweenVars;
        stagger?: number;
        duration?: number;
        ease?: string;
        delay?: number;
    },
    scrollTriggerConfig?: ScrollTriggerConfig
) {
    const ref = useRef<T>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const children = el.querySelectorAll(childSelector);
        if (!children.length) return;

        const ctx = gsap.context(() => {
            gsap.fromTo(
                children,
                config.from,
                {
                    ...config.from,
                    ...config.to,
                    opacity: config.to?.opacity ?? 1,
                    x: config.to?.x ?? 0,
                    y: config.to?.y ?? 0,
                    scale: config.to?.scale ?? 1,
                    duration: config.duration ?? 0.8,
                    ease: config.ease ?? 'power2.out',
                    delay: config.delay ?? 0,
                    stagger: config.stagger ?? 0.15,
                    scrollTrigger: {
                        trigger: el,
                        start: 'top 85%',
                        toggleActions: 'play none none none',
                        ...scrollTriggerConfig,
                    },
                }
            );
        }, el);

        return () => ctx.revert();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return ref;
}

export { gsap, ScrollTrigger };
